import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { getAvailableModels, getDefaultModel, updateConversation } from '../../api/assistant';
import { setLastModelId } from '../../lib/lastModel';
import type { LlmModel } from '../../types';

interface ModelSelectorProps {
  conversationId: string;
  currentModelId?: string;
}

/** Friendlier names for the raw Bedrock provider ids models are grouped by. */
const PROVIDER_LABELS: Record<string, string> = {
  anthropic: 'Claude',
  meta: 'Llama',
  amazon: 'Nova',
  mistral: 'Mistral',
  deepseek: 'DeepSeek',
  cohere: 'Cohere',
  stability: 'Stability AI',
  writer: 'Writer',
  twelvelabs: 'TwelveLabs',
  openai: 'GPT',
  google: 'Gemini',
  qwen: 'Qwen',
  moonshotai: 'Moonshot',
  minimax: 'MiniMax',
  zai: 'Z.AI',
  nvidia: 'NVIDIA',
  ai21: 'AI21',
};

export function providerLabel(provider: string): string {
  return PROVIDER_LABELS[provider.toLowerCase()] ?? provider.charAt(0).toUpperCase() + provider.slice(1);
}

/** Groups models by provider — Claude first (the default provider), then alphabetically by label. */
export function groupByProvider(models: LlmModel[]): [string, LlmModel[]][] {
  const groups = new Map<string, LlmModel[]>();
  for (const model of models) {
    const list = groups.get(model.provider) ?? [];
    list.push(model);
    groups.set(model.provider, list);
  }
  return Array.from(groups.entries()).sort(([a], [b]) => {
    if (a === 'anthropic') return -1;
    if (b === 'anthropic') return 1;
    return providerLabel(a).localeCompare(providerLabel(b));
  });
}

export function ModelSelector({ conversationId, currentModelId }: ModelSelectorProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: models, isLoading } = useQuery({
    queryKey: ['llm-models'],
    queryFn: getAvailableModels,
    staleTime: 5 * 60 * 1000,
  });

  const { data: defaultModel } = useQuery({
    queryKey: ['llm-default-model'],
    queryFn: getDefaultModel,
    staleTime: 5 * 60 * 1000,
  });

  const updateMutation = useMutation({
    mutationFn: (modelId: string) => updateConversation(conversationId, { modelId }),
    onSuccess: (_data, modelId) => {
      setLastModelId(user?.id, modelId);
      queryClient.invalidateQueries({ queryKey: ['assistant-conversation', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['assistant-conversations'] });
    },
  });

  const groups = useMemo(() => groupByProvider(models ?? []), [models]);

  if (isLoading || !models?.length) return null;

  const currentModel = models.find((m: LlmModel) => m.id === currentModelId);
  const defaultModelName = models.find((m: LlmModel) => m.id === defaultModel?.modelId)?.name;
  const defaultLabel = defaultModelName ? `Default (${defaultModelName})` : 'Default';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        disabled={updateMutation.isPending}
        data-testid="model-selector-trigger"
        className="flex h-7 items-center gap-1 rounded-md border border-input bg-background px-2 text-xs w-auto max-w-[180px] disabled:cursor-not-allowed disabled:opacity-50 hover:bg-accent hover:text-accent-foreground"
      >
        <span className="truncate">{currentModel?.name ?? defaultLabel}</span>
        <ChevronDown className="h-3 w-3 opacity-50 shrink-0" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52">
        {groups.map(([provider, providerModels]) => (
          <DropdownMenuSub key={provider}>
            <DropdownMenuSubTrigger className="text-xs">
              {providerLabel(provider)}
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent className="max-h-80 overflow-y-auto w-64">
                {providerModels.map((model) => (
                  <DropdownMenuItem
                    key={model.id}
                    className="text-xs gap-2"
                    data-testid="model-selector-item"
                    onClick={() => updateMutation.mutate(model.id)}
                  >
                    <Check className={cn('h-3.5 w-3.5 shrink-0', model.id === currentModelId ? 'opacity-100' : 'opacity-0')} />
                    <span className="truncate">{model.name}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
